import axios from 'axios'

// The painéis' HTTP layer (layer 0: only HTTP, no React), shared by every
// originator's client (solAgora.ts, solFacil.ts, …).
//
// Absolute host, as in fidcReports.ts; the env override exists only for the
// local harness to point at a stub (the variable keeps its first painel's
// name).
export const HOST =
  process.env.REACT_APP_SOL_AGORA_PAINEL_HOST ?? 'https://api.jgp.com.br'
// fidc-api painel routes (the daily/monthly PDF service)
export const FIDC = `${HOST}/fidc/api/v1/painel`
// Compass credit routes (the tables behind the current report pages)
export const CREDIT = `${HOST}/credit/api/v2/fidc`

const TOKEN_TTL_MS = 5 * 60_000
let tokenCache: { token: string; expiresAt: number } | null = null
let inflightTokenRequest: Promise<string | null> | null = null

const fetchFreshToken = async (): Promise<string | null> => {
  try {
    const res = await fetch('/api/auth/token')
    if (!res.ok) return null
    const { token } = await res.json()
    return typeof token === 'string' ? token : null
  } catch {
    return null
  }
}

// Cache + dedupe: the tabs fire several calls in parallel.
const getToken = async (): Promise<string | null> => {
  const now = Date.now()
  if (tokenCache && now < tokenCache.expiresAt) return tokenCache.token
  if (inflightTokenRequest) return inflightTokenRequest
  inflightTokenRequest = fetchFreshToken().then((token) => {
    if (token) tokenCache = { token, expiresAt: Date.now() + TOKEN_TTL_MS }
    inflightTokenRequest = null
    return token
  })
  return inflightTokenRequest
}

// Called on logout (UserContext): a token must not outlive the session.
export const clearPainelToken = (): void => {
  tokenCache = null
  inflightTokenRequest = null
}

// Never send a painel request without Authorization: no token, no request.
export const getHeaders = async (): Promise<Record<string, string>> => {
  const token = await getToken()
  if (!token) throw new Error('Painel: no auth token')
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

// Dedicated instance (as in peers.ts), so the global axios interceptors stay clean.
export const painelHttp = axios.create()

// On 401: drop the cached token, fetch a fresh one and retry ONCE (_retry
// prevents a loop). A token that expired mid-session would otherwise fail
// every tab at once.
painelHttp.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status
    const cfg = err?.config
    if (status === 401 && cfg && !cfg._retry) {
      tokenCache = null
      cfg._retry = true
      const token = await getToken()
      if (!token) throw new Error('Painel: no auth token')
      cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${token}` }
      return painelHttp.request(cfg)
    }
    return Promise.reject(err)
  }
)

export const get = async <T>(
  url: string,
  params?: Record<string, string>
): Promise<T> => {
  const { data } = await painelHttp.get<T>(url, {
    headers: await getHeaders(),
    params,
  })
  return data
}

export const post = async <T>(url: string, body: unknown): Promise<T> => {
  const { data } = await painelHttp.post<T>(url, body, {
    headers: await getHeaders(),
  })
  return data
}
